import Logger from '../../utils/logger';
import { requestTelemetry } from '../../utils/request';
import platform from '../../utils/platform';
import { ErrorCategories } from '../../utils/errors';
import { getDeviceId } from '../../utils/utils';

let _sequenceNumber = 0;

function collectNetworkInfo() {
  const connectionObject = navigator.connection || navigator.mozConnection || navigator.webkitConnection || {};

  return {
    type: connectionObject.type || 'unknown',
    effectiveType: connectionObject.effectiveType || 'unknown',
    downlink: connectionObject.downlink || 'unknown',
    rtt: connectionObject.rtt || 'unknown',
    downlinkMax: connectionObject.downlinkMax || 'unknown',
  };
}

export default class TelemetryService {
  _player = null;
  _bufferingStartTime = 0;
  _bufferingType = 'initial';

  constructor(_player) {
    this._player = _player;
  }

  start() {
    this._player.addEventListener('playing', this._onPlaying);
    this._player.addEventListener('pause', this._onPause);
    this._player.addEventListener('buffering', this._onBuffering);

    Logger.debug('Telemetry service: started');
  }

  stop() {
    this._bufferingStartTime = 0;
    this._bufferingType = 'initial';

    this._player.removeEventListener('playing', this._onPlaying);
    this._player.removeEventListener('pause', this._onPause);
    this._player.removeEventListener('buffering', this._onBuffering);

    Logger.debug('Telemetry service: stopped');
  }

  _onPlaying = () => {
    const {
      currentTime,
      assetId,
      playlist,
      selectedBitrate,
      currentAdaptation: { videoBitrate, audioBitrate },
    } = this._player.model;

    const {
      videoHeight,
      videoWidth,
    } = this._player.playerElement;

    this._send({
      source: 'APP_ACTION',
      action: 'playback_play',
      module: 'players/shaka/onPlaying/',
      context: {
        playback_position: currentTime,
        playback_video_bitrate: videoBitrate,
        playback_audio_bitrate: audioBitrate,
        screen_height: videoHeight,
        screen_width: videoWidth,
        playlist_url: playlist,
        asset_id: assetId,
        utm_tracking: 'null',
        user_selected_quality: selectedBitrate,
      },
    });
  }

  _onPause = () => {
    const {
      assetId,
      currentAdaptation: { videoBitrate, audioBitrate },
      currentTime,
      playlist,
      selectedBitrate,
    } = this._player.model;

    this._send({
      source: 'APP_ACTION',
      action: 'playback_pause',
      module: 'players/shaka/onPause/',
      context: {
        playlist_url: playlist,
        playback_video_bitrate: videoBitrate,
        playback_audio_bitrate: audioBitrate,
        playback_position: currentTime,
        asset_id: assetId,
        user_selected_quality: selectedBitrate,
      },
    });
  }

  _onBuffering = () => {
    if (this._player.model.isBuffering) {
      // start tracking time if not yet
      if (this._bufferingStartTime === 0) {
        this._bufferingStartTime = Date.now();
        this._bufferingType = this._player.model.bufferingType;
      }
      return;
    }

    if (this._bufferingStartTime === 0) {
      Logger.warn('Telemetry service: got buffering ended event without buffering start event');
    }

    const bufferingType = this._bufferingType;
    const bufferingDuration = this._bufferingStartTime > 0 ? Date.now() - this._bufferingStartTime : 0;
    this._bufferingStartTime = 0;
    this._bufferingType = 'initial';

    const {
      playlist,
      currentTime,
      assetId,
      selectedBitrate,
      currentAdaptation: { videoBitrate, audioBitrate },
      isChromeCasting,
    } = this._player.model;

    if (isChromeCasting) return;

    this._send({
      source: 'APP_ACTION',
      action: 'playback_buffering',
      module: 'players/shaka/onBufferingEnd/',
      context: {
        playback_buffering_type: bufferingType,
        duration: bufferingDuration,
        playlist_url: playlist,
        asset_id: assetId,
        playback_video_bitrate: videoBitrate,
        playback_audio_bitrate: audioBitrate,
        playback_position: currentTime,
        user_selected_quality: selectedBitrate,
      },
    });
  }

  reportCriticalError(e) {
    const {
      isBuffering,
      bufferingType,
      assetId,
      playlist,
      protocol,
    } = this._player.model;

    const errorDetail = e.detail?.detail || e.detail || {};

    this._send({
      module: errorDetail.module,
      action: 'playback_error',
      context: {
        component: '/player/error',
        error_id: errorDetail.message || errorDetail.category || ErrorCategories.default,
        playback_protocol: protocol,
        playback_mode: isBuffering ? bufferingType : 'playing',
        error_code: errorDetail.code || 500,
        asset_id: assetId,
        playlist_url: playlist,
      },
      extra: {
        error: JSON.stringify(errorDetail),
      },
    });
  }

  _send(report) {
    const source = report.source || 'unknown';
    const action = report.action || 'unknown';
    const module = report.module || 'unknown';
    const context = report.context || {};
    const extra = report.extra || {};

    const component = `/player/${module}`;
    const timestamp = Math.trunc(new Date().getTime() / 1000);

    const networkInfo = collectNetworkInfo();

    context.network_type = networkInfo.type;
    context.network_effective_type = networkInfo.effectiveType;
    context.network_downlink = networkInfo.downlink;
    context.network_rtt = networkInfo.rtt;
    context.network_downlink_max = networkInfo.downlinkMax;
    context.device_type = 'web';
    context.device_model = (platform.description || '').toLowerCase();
    context.device_platform = (platform.browser || '').toLowerCase();
    context.device_id = getDeviceId();
    context.browser_name = undefined;
    context.browser_version = undefined;
    context.seqId = undefined;
    context.app_build = '1';
    context.app_version = this._player.model.hipsterPlayerVersion || 'unknown';

    _sequenceNumber += 1;

    requestTelemetry([{
      source,
      component,
      action,
      seqId: _sequenceNumber,
      timestamp,
      context,
      extra,
    }])
      .catch(e => Logger.warn('Failed to send telemetry', e));

    Logger.debug(`Telemetry fired: ${action}`);
  }
}
